import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogShell, DialogHeader, DialogFooter, dialogButtonClass, FormFieldWithError, Input, Textarea, CustomSelect, LoadingButton } from '@redpanda/ui';

/** 與後端（subscription-service feedbackShared）相同的長度上限 */
const TITLE_MAX = 120;
const CONTENT_MAX = 2000;

export interface PostDraft {
  title: string;
  content: string;
  postType?: string;
}

interface PostDialogProps {
  title: string;
  initial?: PostDraft;
  /** 留言才有：類型選項 */
  typeOptions?: { value: string; label: string }[];
  titlePlaceholder: string;
  contentPlaceholder: string;
  /** 發文者顯示名稱；沒登入顯示「訪客」 */
  authorName: string;
  /** 送出（失敗請 throw，對話框保持開著） */
  onSubmit: (draft: PostDraft) => Promise<void>;
  onClose: () => void;
}

/** 新增／編輯留言與許願（取代原本頁面裡的展開表單、就地編輯） */
const PostDialog: React.FC<PostDialogProps> = ({ title, initial, typeOptions, titlePlaceholder, contentPlaceholder, authorName, onSubmit, onClose }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<PostDraft>(initial ?? { title: '', content: '', postType: typeOptions?.[0]?.value });
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);
  const editing = !!initial;

  const titleError = touched && !draft.title.trim() ? t('pages.feedback.title_required') : undefined;
  const contentError = touched && !draft.content.trim() ? t('pages.feedback.content_required') : undefined;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!draft.title.trim() || !draft.content.trim()) return;
    setSaving(true);
    try {
      await onSubmit({ ...draft, title: draft.title.trim(), content: draft.content.trim() });
    } catch {
      // 錯誤訊息由呼叫端顯示；對話框保持開著，內容不會不見
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogShell onClose={onClose} closeDisabled={saving} className="max-w-lg">
      <DialogHeader title={title} onClose={onClose} closeDisabled={saving} closeLabel={t('common.actions.close')} />
      <form onSubmit={handleSubmit} noValidate>
        <div className="p-6 space-y-5">
          <p className="text-sm text-fg-secondary">
            {t('pages.feedback.posting_as')}<span className="ml-1.5 font-medium text-fg">{authorName}</span>
          </p>
          {typeOptions && (
            <FormFieldWithError label={t('pages.feedback.type')} htmlFor="post-type">
              <CustomSelect id="post-type" value={draft.postType} onChange={postType => setDraft(d => ({ ...d, postType }))} options={typeOptions} />
            </FormFieldWithError>
          )}
          <FormFieldWithError label={t('pages.feedback.title')} required error={titleError} htmlFor="post-title">
            <Input
              id="post-title"
              value={draft.title}
              maxLength={TITLE_MAX}
              onChange={e => setDraft(d => ({ ...d, title: e.target.value }))}
              placeholder={titlePlaceholder}
              variant={titleError ? 'error' : 'default'}
              autoFocus
            />
          </FormFieldWithError>
          <FormFieldWithError
            label={t('pages.feedback.content')}
            required
            error={contentError}
            helpText={`${draft.content.length} / ${CONTENT_MAX}`}
            htmlFor="post-content"
          >
            <Textarea
              id="post-content"
              rows={6}
              value={draft.content}
              maxLength={CONTENT_MAX}
              onChange={e => setDraft(d => ({ ...d, content: e.target.value }))}
              placeholder={contentPlaceholder}
            />
          </FormFieldWithError>
        </div>
        <DialogFooter>
          <button type="button" onClick={onClose} disabled={saving} className={dialogButtonClass.cancel}>
            {t('common.actions.cancel')}
          </button>
          <LoadingButton
            type="submit"
            loading={saving}
            isEditMode={editing}
            customText={{ create: t('pages.feedback.submit'), createLoading: t('pages.feedback.submitting'), update: t('common.actions.save'), updateLoading: t('common.status.saving') }}
            t={t}
          />
        </DialogFooter>
      </form>
    </DialogShell>
  );
};

export default PostDialog;
